import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  FaCloudUploadAlt, FaCheckCircle, FaSearch, FaFilter, FaExclamationTriangle,
  FaShoppingCart, FaUtensils, FaGasPump, FaTv, FaCar, FaChartLine,
  FaHome, FaMoneyBillWave, FaCreditCard, FaWifi, FaHeartbeat, FaExchangeAlt,
  FaEllipsisH, FaSun, FaCalendarAlt, FaTimes, FaChevronDown, FaEdit, FaCheck,
  FaFireAlt, FaArrowUp, FaArrowDown, FaWallet, FaGraduationCap, FaShieldAlt,
  FaMusic, FaGamepad, FaFileUpload, FaChevronRight
} from 'react-icons/fa';
import { useTransactions, addTransactions, useDailyUploadStatus, updateTransactionCategory, clearCollection, saveBankBalance } from '../hooks/useFirestore';
import { parseBankStatement, CATEGORY_RULES } from '../utils/bankStatementParser';

// ── Icons & Colors ──────────────────────────────────────────────
const CATEGORY_ICONS = {
  'Shopping': FaShoppingCart, 'Food & Grocery': FaUtensils, 'Fuel': FaGasPump,
  'Subscriptions': FaTv, 'Transport': FaCar, 'Trading': FaChartLine,
  'Rent & Housing': FaHome, 'Income': FaMoneyBillWave, 'EMI & Loans': FaCreditCard,
  'Utilities': FaWifi, 'Healthcare': FaHeartbeat, 'UPI Transfer': FaExchangeAlt,
  'Fund Transfer': FaExchangeAlt, 'Cash': FaWallet, 'Card Payment': FaCreditCard,
  'Entertainment': FaGamepad, 'Wallet/UPI': FaWallet, 'Education': FaGraduationCap,
  'Insurance': FaShieldAlt, 'Other': FaEllipsisH,
};

const CATEGORY_COLORS = {
  'Shopping': 'blue', 'Food & Grocery': 'orange', 'Fuel': 'amber',
  'Subscriptions': 'purple', 'Transport': 'cyan', 'Trading': 'emerald',
  'Rent & Housing': 'indigo', 'Income': 'green', 'EMI & Loans': 'red',
  'Utilities': 'teal', 'Healthcare': 'rose', 'UPI Transfer': 'slate',
  'Fund Transfer': 'slate', 'Cash': 'gray', 'Card Payment': 'gray',
  'Entertainment': 'pink', 'Wallet/UPI': 'violet', 'Education': 'sky',
  'Insurance': 'lime', 'Other': 'gray',
};

const COLORS = {
  blue:    { bg: 'bg-blue-500/10',    text: 'text-blue-400',    border: 'border-blue-500/20',    bar: 'bg-blue-500',    light: 'bg-blue-500/20' },
  orange:  { bg: 'bg-orange-500/10',  text: 'text-orange-400',  border: 'border-orange-500/20',  bar: 'bg-orange-500',  light: 'bg-orange-500/20' },
  amber:   { bg: 'bg-amber-500/10',   text: 'text-amber-400',   border: 'border-amber-500/20',   bar: 'bg-amber-500',   light: 'bg-amber-500/20' },
  purple:  { bg: 'bg-purple-500/10',  text: 'text-purple-400',  border: 'border-purple-500/20',  bar: 'bg-purple-500',  light: 'bg-purple-500/20' },
  cyan:    { bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    border: 'border-cyan-500/20',    bar: 'bg-cyan-500',    light: 'bg-cyan-500/20' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', bar: 'bg-emerald-500', light: 'bg-emerald-500/20' },
  indigo:  { bg: 'bg-indigo-500/10',  text: 'text-indigo-400',  border: 'border-indigo-500/20',  bar: 'bg-indigo-500',  light: 'bg-indigo-500/20' },
  green:   { bg: 'bg-green-500/10',   text: 'text-green-400',   border: 'border-green-500/20',   bar: 'bg-green-500',   light: 'bg-green-500/20' },
  red:     { bg: 'bg-red-500/10',     text: 'text-red-400',     border: 'border-red-500/20',     bar: 'bg-red-500',     light: 'bg-red-500/20' },
  teal:    { bg: 'bg-teal-500/10',    text: 'text-teal-400',    border: 'border-teal-500/20',    bar: 'bg-teal-500',    light: 'bg-teal-500/20' },
  rose:    { bg: 'bg-rose-500/10',    text: 'text-rose-400',    border: 'border-rose-500/20',    bar: 'bg-rose-500',    light: 'bg-rose-500/20' },
  pink:    { bg: 'bg-pink-500/10',    text: 'text-pink-400',    border: 'border-pink-500/20',    bar: 'bg-pink-500',    light: 'bg-pink-500/20' },
  slate:   { bg: 'bg-slate-500/10',   text: 'text-slate-400',   border: 'border-slate-500/20',   bar: 'bg-slate-500',   light: 'bg-slate-500/20' },
  violet:  { bg: 'bg-violet-500/10',  text: 'text-violet-400',  border: 'border-violet-500/20',  bar: 'bg-violet-500',  light: 'bg-violet-500/20' },
  sky:     { bg: 'bg-sky-500/10',     text: 'text-sky-400',     border: 'border-sky-500/20',     bar: 'bg-sky-500',     light: 'bg-sky-500/20' },
  lime:    { bg: 'bg-lime-500/10',    text: 'text-lime-400',    border: 'border-lime-500/20',    bar: 'bg-lime-500',    light: 'bg-lime-500/20' },
  gray:    { bg: 'bg-gray-500/10',    text: 'text-gray-400',    border: 'border-gray-500/20',    bar: 'bg-gray-400',    light: 'bg-gray-500/20' },
};

const fmt = (n) => '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtCompact = (n) => {
  const abs = Math.abs(n);
  if (abs >= 100000) return '₹' + (abs / 100000).toFixed(1) + 'L';
  if (abs >= 1000) return '₹' + (abs / 1000).toFixed(1) + 'K';
  return '₹' + abs.toFixed(0);
};

// ── Main Component ──────────────────────────────────────────────
const BankIntelligence = () => {
  const now = new Date();
  const [monthFilter, setMonthFilter] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const { data: transactions, loading: txnLoading } = useTransactions(monthFilter);
  const { uploaded: yesterdayUploaded, loading: statusLoading } = useDailyUploadStatus();

  // Upload
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [bankOverride, setBankOverride] = useState(null);
  const [parseError, setParseError] = useState(null);
  const fileRef = useRef(null);
  const [showDebug, setShowDebug] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editCategory, setEditCategory] = useState('');

  // ── Upload & Parse ──────────────────────────────────────────
  const handleFile = useCallback(async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      setParseError('Please upload a PDF file');
      return;
    }
    setParseError(null);
    setParsed(null);
    setSaveResult(null);
    setParsing(true);
    try {
      const result = await parseBankStatement(file, bankOverride);
      setParsed(result);
      if (result.transactions.length === 0) {
        setParseError('No transactions found. Expand "Raw Text" below to debug.');
        setShowDebug(true);
      }
    } catch (err) {
      setParseError(`Parse failed: ${err.message}`);
    } finally {
      setParsing(false);
    }
  }, [bankOverride]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleSave = useCallback(async () => {
    if (!parsed) return;
    setSaving(true);
    try {
      const result = await addTransactions(parsed.transactions);
      setSaveResult(result);
      // Save closing balance if extracted
      if (parsed.closingBalance && parsed.bank) {
        try {
          await saveBankBalance(parsed.bank, parsed.closingBalance, new Date().toISOString());
        } catch (e) { console.error('Failed to save bank balance:', e); }
      }
      // Auto-switch to the month with most transactions
      if (parsed.transactions.length > 0) {
        const mc = {};
        parsed.transactions.forEach(t => { const m = t.date.slice(0, 7); mc[m] = (mc[m] || 0) + 1; });
        const top = Object.entries(mc).sort((a, b) => b[1] - a[1])[0]?.[0];
        if (top) setMonthFilter(top);
      }
      setParsed(null);
    } catch (err) {
      setParseError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [parsed]);

  // ── Analytics ───────────────────────────────────────────────
  const analytics = useMemo(() => {
    if (!transactions.length) return null;

    const debits = transactions.filter(t => t.type === 'debit');
    const credits = transactions.filter(t => t.type === 'credit');
    const totalSpend = debits.reduce((s, t) => s + (t.amount || 0), 0);
    const totalIncome = credits.reduce((s, t) => s + (t.amount || 0), 0);

    // Category breakdown (expenses only)
    const byCategory = {};
    debits.forEach(t => {
      const cat = t.category || 'Other';
      if (!byCategory[cat]) byCategory[cat] = { total: 0, count: 0, color: t.categoryColor || CATEGORY_COLORS[cat] || 'gray' };
      byCategory[cat].total += t.amount || 0;
      byCategory[cat].count += 1;
    });
    const categoryList = Object.entries(byCategory)
      .map(([name, data]) => ({ name, ...data, pct: totalSpend > 0 ? (data.total / totalSpend) * 100 : 0 }))
      .sort((a, b) => b.total - a.total);

    // Yesterday's stats (use local dates to avoid UTC day-shift)
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    const yStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
    const yTxns = transactions.filter(t => {
      const d = new Date(t.date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` === yStr;
    });
    const yesterdaySpend = yTxns.filter(t => t.type === 'debit').reduce((s, t) => s + (t.amount || 0), 0);
    const yesterdayIncome = yTxns.filter(t => t.type === 'credit').reduce((s, t) => s + (t.amount || 0), 0);

    // 7-day avg
    const sevenAgo = new Date(); sevenAgo.setDate(sevenAgo.getDate() - 7);
    const last7 = transactions.filter(t => new Date(t.date) >= sevenAgo && t.type === 'debit');
    const avg7Day = last7.length > 0 ? last7.reduce((s, t) => s + (t.amount || 0), 0) / 7 : 0;

    // Daily budget
    const dailyBudget = totalIncome > 0 ? (totalIncome * 0.7) / 30 : 5000;

    // Top merchants
    const merchantMap = {};
    debits.forEach(t => {
      const key = (t.description || '').substring(0, 35);
      merchantMap[key] = (merchantMap[key] || 0) + (t.amount || 0);
    });
    const topMerchants = Object.entries(merchantMap)
      .sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([name, total]) => ({ name, total }));

    // Day heatmap (use local date)
    const daySpend = {};
    debits.forEach(t => {
      const d = new Date(t.date);
      daySpend[d.getDate()] = (daySpend[d.getDate()] || 0) + (t.amount || 0);
    });
    const maxDaySpend = Math.max(...Object.values(daySpend), 1);

    return {
      totalSpend, totalIncome, savings: totalIncome - totalSpend,
      txnCount: transactions.length, debitCount: debits.length, creditCount: credits.length,
      yesterdaySpend, yesterdayIncome, avg7Day, dailyBudget,
      categoryList, topMerchants, daySpend, maxDaySpend,
    };
  }, [transactions]);

  // ── Filtered ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return transactions.filter(t => {
      if (search && !(t.description || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryFilter !== 'All' && t.category !== categoryFilter) return false;
      if (typeFilter !== 'All' && t.type !== typeFilter) return false;
      return true;
    });
  }, [transactions, search, categoryFilter, typeFilter]);

  const allCategories = useMemo(() => {
    return ['All', ...new Set(transactions.map(t => t.category).filter(Boolean))].sort();
  }, [transactions]);

  // ── Category Edit ───────────────────────────────────────────
  const handleCategoryEdit = async (txn) => {
    if (editingId === txn.id) {
      if (editCategory && editCategory !== txn.category) {
        await updateTransactionCategory(txn.id, editCategory, CATEGORY_COLORS[editCategory] || 'gray');
      }
      setEditingId(null);
    } else {
      setEditingId(txn.id);
      setEditCategory(txn.category);
    }
  };

  const isHour = now.getHours();
  const showDailyPrompt = !statusLoading && !yesterdayUploaded && isHour >= 6 && isHour <= 14;

  // Selected month label
  const [mYear, mMonth] = monthFilter.split('-').map(Number);
  const monthLabel = new Date(mYear, mMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-8 text-frost-900">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="text-[10px] font-semibold text-accent-400 uppercase tracking-[0.2em] mb-1">Financial Intelligence</p>
          <h1 className="text-2xl font-bold tracking-tighter text-frost-900 leading-none">Bank Statements</h1>
          <p className="text-frost-400 mt-2 font-medium text-sm">Upload, parse & analyze your bank statements automatically</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={monthFilter}
            onChange={e => setMonthFilter(e.target.value)}
            className="bg-white/[0.03] border border-white/[0.06] px-5 py-3 rounded-2xl text-xs font-bold text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500 cursor-pointer "
          >
            {Array.from({ length: 12 }, (_, i) => {
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
              const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
              return <option key={val} value={val}>{label}</option>;
            })}
          </select>
          {transactions.length > 0 && (
            <button
              onClick={async () => {
                if (window.confirm(`Delete all ${transactions.length} transactions for ${monthLabel}? This cannot be undone.`)) {
                  await clearCollection('transactions');
                }
              }}
              className="text-[10px] font-bold text-gold-400 hover:text-gold-500 px-3 py-3 rounded-2xl hover:bg-gold-500/10 transition-all"
            >
              Clear Data
            </button>
          )}
        </div>
      </div>

      {/* ── Daily Prompt ─────────────────────────────────────── */}
      {showDailyPrompt && (
        <div className="bg-white/[0.03] border border-gold-500/20 rounded-2xl p-6 flex items-center gap-5 ">
          <div className="w-14 h-14 bg-white/[0.03] rounded-2xl flex items-center justify-center  flex-shrink-0">
            <FaSun className="text-gold-400 text-xl" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-gold-300">Good morning! Time to upload yesterday's statement</p>
            <p className="text-xs text-gold-400/70 font-medium mt-0.5">Drop your bank PDF below to keep your finances in check</p>
          </div>
          <FaChevronDown className="text-gold-400 animate-bounce flex-shrink-0" />
        </div>
      )}

      {/* ── Success Banner ───────────────────────────────────── */}
      {saveResult && (
        <div className="bg-mint-500/10 border border-mint-500/20 rounded-2xl p-5 flex items-center gap-4 ">
          <div className="w-11 h-11 bg-mint-500/20 rounded-xl flex items-center justify-center">
            <FaCheckCircle className="text-mint-400 text-lg" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-mint-300">{saveResult.added} transactions saved successfully</p>
            {saveResult.duplicates > 0 && <p className="text-xs text-mint-400 font-medium mt-0.5">{saveResult.duplicates} duplicates skipped</p>}
          </div>
          <button onClick={() => setSaveResult(null)} className="text-mint-300 hover:text-mint-400 transition-colors p-2"><FaTimes /></button>
        </div>
      )}

      {/* ── Overview Cards ───────────────────────────────────── */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <OverviewCard label="Money In" value={fmt(analytics.totalIncome)} count={analytics.creditCount}
            icon={FaArrowDown} gradient="from-mint-500 to-mint-600" />
          <OverviewCard label="Money Out" value={fmt(analytics.totalSpend)} count={analytics.debitCount}
            icon={FaArrowUp} gradient="from-gold-500 to-gold-600" />
          <OverviewCard label="Net Savings" value={fmt(Math.abs(analytics.savings))}
            count={analytics.savings >= 0 ? 'Positive' : 'Negative'}
            icon={FaWallet} gradient={analytics.savings >= 0 ? "from-mint-500 to-mint-600" : "from-gold-500 to-gold-600"} />
          <OverviewCard label="Transactions" value={analytics.txnCount} count={monthLabel}
            icon={FaExchangeAlt} gradient="from-accent-500 to-accent-600" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── Left Column ────────────────────────────────────── */}
        <div className="lg:col-span-4 space-y-6">
          {/* Upload Card */}
          <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  overflow-hidden">
            <div className="p-6 border-b border-white/[0.04] bg-gradient-to-r from-surface-50 to-surface-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-accent-500/10 rounded-xl flex items-center justify-center">
                  <FaFileUpload className="text-accent-400 text-sm" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-frost-900 leading-none">Upload Statement</h3>
                  <p className="text-[10px] text-frost-400 font-medium mt-0.5">Any Indian bank PDF</p>
                </div>
              </div>
            </div>

            <div
              className={`p-8 flex flex-col items-center text-center transition-all duration-300 cursor-pointer ${
                isDragging ? 'bg-accent-500/10/50' : 'hover:bg-white/[0.03]'
              }`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${
                isDragging ? 'bg-accent-500/20 scale-110' : 'bg-white/[0.03]'
              }`}>
                <FaCloudUploadAlt className={`text-2xl transition-colors ${isDragging ? 'text-accent-400' : 'text-frost-300'}`} />
              </div>
              <p className="text-xs font-bold text-frost-900">Drop PDF here or click to browse</p>
              <p className="text-[10px] text-frost-400 mt-1">
                IDFC, Slice, HDFC, SBI, ICICI, Axis & more
              </p>
              <input type="file" ref={fileRef} accept=".pdf" className="hidden" onChange={handleFileInput} />
            </div>

            {/* Bank selector */}
            <div className="px-6 pb-5 flex gap-2 flex-wrap">
              {['Auto', 'IDFC First', 'Slice', 'HDFC', 'SBI', 'ICICI', 'Axis'].map(b => (
                <button
                  key={b}
                  onClick={() => setBankOverride(b === 'Auto' ? null : b)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                    (b === 'Auto' && !bankOverride) || bankOverride === b
                      ? 'bg-dark-600 text-white '
                      : 'bg-white/[0.03] text-frost-400 hover:bg-white/[0.04]'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>

            {parsing && (
              <div className="px-6 pb-5 flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-white/[0.06] border-t-accent-500 rounded-full animate-spin"></div>
                <span className="text-xs font-bold text-frost-400">Parsing PDF...</span>
              </div>
            )}
          </div>

          {/* Parse Error */}
          {parseError && (
            <div className="bg-gold-500/10 border border-gold-500/20 rounded-2xl p-4 flex items-start gap-3">
              <FaExclamationTriangle className="text-gold-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-bold text-gold-400">{parseError}</p>
              </div>
              <button onClick={() => setParseError(null)} className="text-red-300 hover:text-gold-400"><FaTimes className="text-xs" /></button>
            </div>
          )}

          {/* Parse Preview */}
          {parsed && parsed.transactions.length > 0 && (
            <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  overflow-hidden">
              <div className="p-5 border-b border-white/[0.04]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-accent-500/100"></div>
                    <h4 className="font-bold text-sm text-frost-900">Parsed: {parsed.bank}</h4>
                  </div>
                  <span className="text-xs font-bold text-frost-400">{parsed.stats.totalTransactions} transactions</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <MiniStat label="Debited" value={fmt(parsed.stats.totalDebit)} color="red" />
                  <MiniStat label="Credited" value={fmt(parsed.stats.totalCredit)} color="emerald" />
                  <MiniStat label="Net" value={fmt(Math.abs(parsed.stats.net))} color={parsed.stats.net >= 0 ? 'emerald' : 'red'} />
                </div>
                {parsed.closingBalance && (
                  <div className="mt-2 bg-accent-500/10 rounded-xl p-3 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-accent-400 uppercase">Closing Balance</span>
                    <span className="text-sm font-bold text-accent-300">{fmt(parsed.closingBalance)}</span>
                  </div>
                )}
              </div>

              {/* Preview list */}
              <div className="max-h-72 overflow-y-auto divide-y divide-white/[0.04]">
                {parsed.transactions.slice(0, 20).map((t, i) => {
                  const colors = COLORS[t.categoryColor] || COLORS.gray;
                  return (
                    <div key={i} className="px-5 py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors">
                      <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        {React.createElement(CATEGORY_ICONS[t.category] || FaEllipsisH, { className: `text-xs ${colors.text}` })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-frost-900 truncate">{t.description}</p>
                        <p className="text-[10px] text-frost-400 font-medium">
                          {new Date(t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {t.category}
                        </p>
                      </div>
                      <span className={`text-xs font-bold ${t.type === 'credit' ? 'text-mint-400' : 'text-frost-900'}`}>
                        {t.type === 'credit' ? '+' : '-'}{fmt(t.amount)}
                      </span>
                    </div>
                  );
                })}
                {parsed.transactions.length > 20 && (
                  <div className="px-5 py-3 text-center">
                    <span className="text-[10px] font-bold text-frost-400">+{parsed.transactions.length - 20} more</span>
                  </div>
                )}
              </div>

              <div className="p-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full py-3.5 bg-dark-600 text-white text-xs font-bold uppercase tracking-wider rounded-2xl hover:bg-frost-800 transition-all active:scale-[0.98] disabled:opacity-50 shadow-glow"
                >
                  {saving ? 'Saving...' : `Save ${parsed.stats.totalTransactions} Transactions`}
                </button>
              </div>
            </div>
          )}

          {/* Debug Raw Text */}
          {parsed && parsed.rawLines && (
            <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  overflow-hidden">
              <button onClick={() => setShowDebug(!showDebug)} className="w-full p-4 flex items-center justify-between hover:bg-white/[0.03] transition-colors">
                <span className="text-[10px] font-bold text-frost-400 uppercase tracking-wider">Raw Text ({parsed.rawLines.length} lines)</span>
                <FaChevronDown className={`text-frost-300 text-[10px] transition-transform ${showDebug ? 'rotate-180' : ''}`} />
              </button>
              {showDebug && (
                <div className="px-4 pb-4">
                  <div className="max-h-64 overflow-y-auto bg-dark-600 rounded-xl p-3">
                    <pre className="text-[9px] text-green-400 font-mono whitespace-pre-wrap leading-relaxed">
                      {parsed.rawLines.map((l, i) => `${String(i + 1).padStart(3)} │ ${l}`).join('\n')}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Category Breakdown */}
          {analytics && analytics.categoryList.length > 0 && (
            <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  overflow-hidden">
              <div className="p-5 border-b border-white/[0.04]">
                <h4 className="font-bold text-sm text-frost-900">Where your money went</h4>
                <p className="text-[10px] text-frost-400 font-medium mt-0.5">{analytics.categoryList.length} categories this month</p>
              </div>
              <div className="p-5 space-y-4">
                {analytics.categoryList.map(cat => {
                  const Icon = CATEGORY_ICONS[cat.name] || FaEllipsisH;
                  const c = COLORS[cat.color] || COLORS.gray;
                  return (
                    <div key={cat.name}>
                      <div className="flex items-center gap-3 mb-1.5">
                        <div className={`w-8 h-8 ${c.bg} rounded-lg flex items-center justify-center`}>
                          <Icon className={`text-xs ${c.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-frost-900">{cat.name}</span>
                            <span className="text-xs font-bold text-frost-900">{fmt(cat.total)}</span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] text-frost-400 font-medium">{cat.count} txns</span>
                            <span className={`text-[10px] font-bold ${c.text}`}>{cat.pct.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="h-1.5 bg-white/[0.03] rounded-full overflow-hidden ml-11">
                        <div className={`h-full ${c.bar} rounded-full transition-all duration-700`} style={{ width: `${cat.pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Daily Spending Card */}
          {analytics && (
            <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  p-5 space-y-4">
              <h4 className="font-bold text-sm text-frost-900">Yesterday</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gold-500/10 rounded-2xl p-3">
                  <p className="text-[10px] font-bold text-gold-400 uppercase">Spent</p>
                  <p className="text-lg font-bold text-gold-400">{fmt(analytics.yesterdaySpend)}</p>
                </div>
                <div className="bg-mint-500/10 rounded-2xl p-3">
                  <p className="text-[10px] font-bold text-mint-300 uppercase">Received</p>
                  <p className="text-lg font-bold text-mint-400">{fmt(analytics.yesterdayIncome)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs bg-white/[0.03] rounded-xl p-3">
                <span className="font-medium text-frost-400">7-day daily avg</span>
                <span className="font-bold text-frost-900">{fmt(analytics.avg7Day)}</span>
              </div>
              {/* Budget bar */}
              <div>
                <div className="flex justify-between text-[10px] font-bold mb-1">
                  <span className="text-frost-400">Daily Budget</span>
                  <span>{fmt(analytics.yesterdaySpend)} / {fmt(analytics.dailyBudget)}</span>
                </div>
                <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      analytics.yesterdaySpend / analytics.dailyBudget > 1 ? 'bg-red-500' :
                      analytics.yesterdaySpend / analytics.dailyBudget > 0.7 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(100, (analytics.yesterdaySpend / analytics.dailyBudget) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Right Column ───────────────────────────────────── */}
        <div className="lg:col-span-8 space-y-6">
          {/* Heatmap + Top Merchants row */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Heatmap */}
              <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FaCalendarAlt className="text-frost-300 text-xs" />
                  <h4 className="font-bold text-sm text-frost-900">Daily Spend</h4>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {Array.from({ length: new Date(mYear, mMonth, 0).getDate() }, (_, i) => {
                    const day = i + 1;
                    const spent = analytics.daySpend[day] || 0;
                    const intensity = spent / analytics.maxDaySpend;
                    return (
                      <div key={day} className="relative group">
                        <div
                          className="w-full aspect-square rounded-lg flex items-center justify-center text-[9px] font-bold cursor-default transition-all hover:scale-110 hover:z-10"
                          style={{
                            backgroundColor: spent > 0
                              ? `rgba(239, 68, 68, ${0.08 + intensity * 0.55})`
                              : 'rgba(255,255,255,0.04)',
                            color: spent > 0 && intensity > 0.4 ? '#fff' : '#9ca3af'
                          }}
                        >{day}</div>
                        {spent > 0 && (
                          <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-dark-600 text-white text-[9px] font-bold px-2.5 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-30 pointer-events-none shadow-lg">
                            {fmt(spent)}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-graphite-900"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-3 text-[9px] font-medium text-frost-400">
                  <span>Less</span>
                  <div className="flex gap-1">
                    {[0.1, 0.25, 0.45, 0.7, 1].map((v, i) => (
                      <div key={i} className="w-3 h-3 rounded" style={{ backgroundColor: `rgba(239, 68, 68, ${0.08 + v * 0.55})` }} />
                    ))}
                  </div>
                  <span>More</span>
                </div>
              </div>

              {/* Top Merchants */}
              <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  p-5">
                <h4 className="font-bold text-sm text-frost-900 mb-4">Top Spends</h4>
                <div className="space-y-3">
                  {analytics.topMerchants.map((m, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold text-white ${
                        i === 0 ? 'bg-red-500' : i === 1 ? 'bg-orange-500' : i === 2 ? 'bg-amber-500' : 'bg-gray-400'
                      }`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-frost-900 truncate">{m.name}</p>
                      </div>
                      <span className="text-xs font-bold text-frost-900">{fmtCompact(m.total)}</span>
                    </div>
                  ))}
                  {analytics.topMerchants.length === 0 && (
                    <p className="text-xs text-frost-400 text-center py-4">No data yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Transaction Table */}
          <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  overflow-hidden">
            {/* Search bar */}
            <div className="p-5 border-b border-white/[0.04] flex flex-col md:flex-row items-start md:items-center gap-3">
              <div className="relative flex-1 w-full">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-frost-300 text-xs" />
                <input
                  type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search transactions..."
                  className="w-full pl-10 pr-4 py-3 bg-white/[0.03] border border-white/[0.04] rounded-xl text-xs font-medium text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500/30 transition-all placeholder:text-frost-300"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                  showFilters ? 'bg-dark-600 text-white' : 'bg-white/[0.03] text-frost-500 hover:bg-white/[0.04]'
                }`}
              >
                <FaFilter className="text-[10px]" /> Filters
              </button>
            </div>

            {showFilters && (
              <div className="px-5 py-3 border-b border-white/[0.04] flex flex-wrap gap-2 bg-white/[0.02]">
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                  className="bg-white/[0.03] border border-white/[0.06] px-3 py-2 rounded-lg text-[11px] font-bold text-frost-700 cursor-pointer">
                  {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                  className="bg-white/[0.03] border border-white/[0.06] px-3 py-2 rounded-lg text-[11px] font-bold text-frost-700 cursor-pointer">
                  <option value="All">All Types</option>
                  <option value="debit">Debits Only</option>
                  <option value="credit">Credits Only</option>
                </select>
                {(categoryFilter !== 'All' || typeFilter !== 'All') && (
                  <button onClick={() => { setCategoryFilter('All'); setTypeFilter('All'); }}
                    className="text-[11px] font-bold text-red-500 hover:text-gold-400 px-2 transition-colors">Clear</button>
                )}
              </div>
            )}

            {/* Table content */}
            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              {txnLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-3 border-white/[0.06] border-t-accent-500 rounded-full animate-spin"></div>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-frost-400">
                  <div className="w-16 h-16 bg-white/[0.03] rounded-2xl flex items-center justify-center mb-4">
                    <FaExchangeAlt className="text-2xl text-frost-300" />
                  </div>
                  <p className="text-sm font-bold text-frost-900">No transactions for {monthLabel}</p>
                  <p className="text-xs font-medium mt-1">Upload a statement or switch months</p>
                </div>
              ) : (
                <div className="divide-y divide-white/[0.04]">
                  {filtered.map(txn => {
                    const date = new Date(txn.date);
                    const catColor = COLORS[txn.categoryColor || CATEGORY_COLORS[txn.category] || 'gray'] || COLORS.gray;
                    const Icon = CATEGORY_ICONS[txn.category] || FaEllipsisH;
                    return (
                      <div key={txn.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-white/[0.03] transition-colors group">
                        {/* Category icon */}
                        <div className={`w-10 h-10 ${catColor.bg} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                          <Icon className={`text-sm ${catColor.text}`} />
                        </div>

                        {/* Description & details */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-frost-900 truncate group-hover:text-accent-400 transition-colors">
                            {txn.description}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-frost-400 font-medium">
                              {date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                            <span className="text-frost-400">·</span>
                            <span className={`text-[10px] font-bold ${catColor.text}`}>{txn.bank}</span>
                          </div>
                        </div>

                        {/* Category badge + edit */}
                        <div className="hidden md:flex items-center gap-1.5">
                          {editingId === txn.id ? (
                            <select value={editCategory} onChange={e => setEditCategory(e.target.value)}
                              className="text-[10px] font-bold border border-white/[0.06] rounded-lg px-2 py-1 bg-dark-500 text-frost-900">
                              {Object.keys(CATEGORY_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          ) : (
                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${catColor.bg} ${catColor.text}`}>
                              {txn.category}
                            </span>
                          )}
                          <button onClick={() => handleCategoryEdit(txn)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-frost-300 hover:text-accent-400">
                            {editingId === txn.id ? <FaCheck className="text-[10px]" /> : <FaEdit className="text-[10px]" />}
                          </button>
                        </div>

                        {/* Amount */}
                        <div className="text-right flex-shrink-0">
                          <p className={`text-sm font-bold ${txn.type === 'credit' ? 'text-mint-400' : 'text-frost-900'}`}>
                            {txn.type === 'credit' ? '+' : '-'}{fmt(txn.amount)}
                          </p>
                          <p className={`text-[10px] font-bold ${txn.type === 'credit' ? 'text-mint-300' : 'text-frost-300'}`}>
                            {txn.type === 'credit' ? 'CREDIT' : 'DEBIT'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {filtered.length > 0 && (
              <div className="px-5 py-3.5 border-t border-white/[0.04] flex items-center justify-between bg-white/[0.02]">
                <span className="text-[10px] font-bold text-frost-400">
                  {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
                  {categoryFilter !== 'All' ? ` in ${categoryFilter}` : ''}
                </span>
                <span className="text-[10px] font-bold text-frost-400">
                  Total: {fmt(filtered.filter(t => t.type === 'debit').reduce((s, t) => s + t.amount, 0))} spent
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sub Components ──────────────────────────────────────────────
const OverviewCard = ({ label, value, count, icon: Icon, gradient }) => (
  <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06]  p-5 relative overflow-hidden group hover:shadow-md transition-all">
    <div className={`absolute -top-6 -right-6 w-20 h-20 bg-gradient-to-br ${gradient} rounded-full opacity-10 group-hover:opacity-15 transition-opacity`}></div>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
        <Icon className="text-white text-sm" />
      </div>
    </div>
    <p className="text-xl md:text-2xl font-bold text-frost-900 tracking-tight">{value}</p>
    <div className="flex items-center justify-between mt-1">
      <p className="text-[10px] font-bold text-frost-400 uppercase tracking-wider">{label}</p>
      <p className="text-[10px] font-bold text-frost-400">{count}</p>
    </div>
  </div>
);

const MINI_COLORS = {
  red: { bg: 'bg-red-500/10', text: 'text-red-400' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
};

const MiniStat = ({ label, value, color }) => {
  const c = MINI_COLORS[color] || { bg: 'bg-gray-500/10', text: 'text-gray-400' };
  return (
    <div className={`${c.bg} rounded-xl p-3 text-center`}>
      <p className={`text-[9px] font-bold ${c.text} uppercase`}>{label}</p>
      <p className={`text-xs font-bold ${c.text} mt-0.5`}>{value}</p>
    </div>
  );
};

export default BankIntelligence;
